import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axiosClient from "../api/axiosClient";
import StarRating from "../components/StarRating";
import CartFloatingButton from "../components/CartFloatingButton";
import "./AvailableBikes.css";

/* ── Helper: find active override price for a specific bike in date range ── */
function getBikeOverridePrice(bikeId, overrides, startDate, endDate) {
  if (!overrides || overrides.length === 0 || !bikeId || !startDate || !endDate) return null;
  const start = new Date(startDate);
  const end = new Date(endDate);
  let bestOverride = null;

  for (const o of overrides) {
    if (!o.isActive) continue;
    const oStart = new Date(o.startDate);
    const oEnd = new Date(o.endDate);
    if (oStart <= end && oEnd >= start) {
      const targetBikeId =
        o.bikeId?._id ||
        o.bikeId ||
        (o.bikeIds?.[0]?._id ? o.bikeIds[0]._id : o.bikeIds?.[0]);

      const isSpecificToThisBike = targetBikeId && String(targetBikeId) === String(bikeId);
      const isGlobal = !targetBikeId && (!o.bikeIds || o.bikeIds.length === 0);

      if (isSpecificToThisBike) {
        if (bestOverride === null || !bestOverride.isSpecific || o.pricePerDay > bestOverride.price) {
          bestOverride = { price: o.pricePerDay, isSpecific: true };
        }
      } else if (isGlobal) {
        if (bestOverride === null || (!bestOverride.isSpecific && o.pricePerDay > bestOverride.price)) {
          bestOverride = { price: o.pricePerDay, isSpecific: false };
        }
      }
    }
  }

  return bestOverride ? bestOverride.price : null;
}


const getBikeImageUrl = (image) => {
  if (!image) return "/placeholder-bike.jpg";

  const cleanImage = image.trim();

  if (
    cleanImage.startsWith("http://") ||
    cleanImage.startsWith("https://")
  ) {
    return cleanImage;
  }

  return `https://bros-bike-rental-api.onrender.com/${cleanImage.replace(
    /^\/+/,
    ""
  )}`;
};

function AvailableBikes() {
  const [bikes, setBikes] = useState([]);
  const [filteredBikes, setFilteredBikes] = useState([]);
  const [loading, setLoading] = useState(true);

  // ⭐ Reviews state
  const [reviewsMap, setReviewsMap] = useState({});
  const [openBikeId, setOpenBikeId] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);

  // ⭐ Ratings state
  const [ratings, setRatings] = useState({});

  // 💰 Price overrides state
  const [overrides, setOverrides] = useState([]);

  // Pull-to-refresh
  const startY = useRef(0);
  const pulling = useRef(false);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const start = searchParams.get("start");
  const end = searchParams.get("end");

  /* ---------- FETCH BIKES ---------- */
  const fetchBikes = () => {
    if (!start || !end) return;

    setLoading(true);

    axiosClient
      .post("/availability", { startDate: start, endDate: end })
      .then((res) => {
        setBikes(res.data);
        setFilteredBikes(res.data);
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  useEffect(fetchBikes, [start, end]);

  /* ---------- FETCH PRICE OVERRIDES ---------- */
  useEffect(() => {
    if (!start || !end) return;
    axiosClient
      .get(`/price-overrides/range?startDate=${start}&endDate=${end}`)
      .then((res) => {
        const fetched = res.data.overrides || [];
        setOverrides(fetched);
      })
      .catch(() => {
        setOverrides([]);
      });
  }, [start, end]);

  /* ---------- FETCH RATINGS ---------- */
  useEffect(() => {
    bikes.forEach((bike) => {
      axiosClient
        .get(`/reviews/bike/${bike._id}/rating`)
        .then((res) => {
          setRatings((prev) => ({
            ...prev,
            [bike._id]: res.data,
          }));
        })
        .catch(() => { });
    });
  }, [bikes]);

  /* ---------- LOAD REVIEWS ---------- */
  const loadReviews = (bikeId) => {
    setOpenBikeId(bikeId);
    if (reviewsMap[bikeId]) return; // already fetched

    setReviewLoading(true);
    axiosClient
      .get(`/reviews/bike/${bikeId}`)
      .then((res) => {
        setReviewsMap((prev) => ({ ...prev, [bikeId]: res.data }));
      })
      .catch(() => {
        setReviewsMap((prev) => ({ ...prev, [bikeId]: [] }));
      })
      .finally(() => setReviewLoading(false));
  };

  const closeReviews = () => setOpenBikeId(null);

  const openBike = bikes.find((b) => b._id === openBikeId);
  const openReviews = openBikeId ? (reviewsMap[openBikeId] ?? null) : null;

  /* ---------- PULL TO REFRESH ---------- */
  const handleTouchStart = (e) => {
    startY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e) => {
    const diff = e.touches[0].clientY - startY.current;
    if (window.scrollY === 0 && diff > 80) pulling.current = true;
  };

  const handleTouchEnd = () => {
    if (pulling.current) {
      pulling.current = false;
      navigator.vibrate?.(30);
      fetchBikes();
    }
  };

  return (
    <div
      className="available-page"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <CartFloatingButton />

      {/* HEADER */}
      <div className="page-header">
        <button className="page-back-btn" onClick={() => navigate(-1)} title="Go back">
          <span className="back-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 5l-7 7 7 7"/>
            </svg>
          </span>
          Back
        </button>
        <h1>Available Bikes</h1>
      </div>

      {/* GRID */}
      <div className="bike-grid">
        {loading &&
          [...Array(6)].map((_, i) => (
            <div className="bike-card skeleton" key={i}>
              <div className="image-wrapper skeleton-box" />
              <div className="card-content">
                <div className="skeleton-line title" />
                <div className="skeleton-line price" />
                <div className="skeleton-button" />
              </div>
            </div>
          ))}

        {!loading &&
          filteredBikes.map((bike) => {
            // Check both the database availability AND overlapping bookings
            const isUnavailable = bike.isAvailable === false || bike.isDisabled || bike.isBooked || bike.isBlocked;
            const rating = ratings[bike._id];

            return (
              <div
                key={bike._id}
                className={`bike-card ${isUnavailable ? "unavailable" : ""}`}
              >
                <div className="image-wrapper">
                  <img
                    src={getBikeImageUrl(bike.image)}
                    alt={bike.name}
                  />
                  {isUnavailable && (
                    <span className="badge">Unavailable</span>
                  )}
                </div>

                <div className="card-content">
                  <div className="title-row">
                    <h2>{bike.name}</h2>
                    <span className="type-chip">{bike.type}</span>
                  </div>

                  {/* ⭐ STAR RATING */}
                  {rating?.totalReviews > 0 ? (
                    <div style={styles.rating}>
                      <StarRating rating={rating.averageRating} size={16} />
                      <span style={{ marginLeft: "6px", color: "#666" }}>
                        {rating.averageRating} ({rating.totalReviews})
                      </span>
                    </div>
                  ) : (
                    <span style={{ color: "#999" }}>No reviews yet</span>
                  )}

                  {(() => {
                    const overridePrice = getBikeOverridePrice(bike._id, overrides, start, end);
                    const intendedPrice = overridePrice || bike.pricePerDay;
                    const strikePrice = Math.round(intendedPrice * 1.4);

                    return (
                      <div className="price-row">
                        {overridePrice ? (
                          <>
                            <span className="price surge-price">₹ {intendedPrice}</span>
                            <span className="price-original-strike">₹ {strikePrice}</span>
                            <span className="per-day">/ day</span>
                            <span className="surge-badge">🔥 Special Price</span>
                          </>
                        ) : (
                          <>
                            <span className="price">₹ {intendedPrice}</span>
                            <span className="price-original-strike">₹ {strikePrice}</span>
                            <span className="per-day">/ day</span>
                          </>
                        )}
                      </div>
                    );
                  })()}


                  <button
                    className={isUnavailable ? "btn-disabled" : "btn-primary"}
                    disabled={isUnavailable}
                    onClick={() =>
                      navigate(
                        `/booking?bike=${bike._id}&start=${start}&end=${end}`
                      )
                    }
                  >
                    {isUnavailable ? "Unavailable" : "Book Now"}
                  </button>

                  {/* ⭐ VIEW REVIEWS */}
                  <button
                    className="review-trigger-btn"
                    onClick={() => loadReviews(bike._id)}
                  >
                    <span>⭐ Reviews</span>
                    {rating?.totalReviews > 0 && (
                      <span className="review-count-badge">{rating.totalReviews}</span>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
      </div>

      {/* ⭐ REVIEW MODAL — rendered outside the grid */}
      {openBikeId && (
        <div className="review-modal-backdrop" onClick={closeReviews}>
          <div
            className="review-modal-panel"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="review-modal-header">
              <div>
                <h3 className="review-modal-title">
                  {openBike?.name ?? ""}
                </h3>
                <p className="review-modal-subtitle">Customer Reviews</p>
              </div>
              <button className="review-modal-close" onClick={closeReviews}>
                ✕
              </button>
            </div>

            {/* Body */}
            <div className="review-modal-body">
              {reviewLoading && (
                <div className="review-inline-loader">
                  <div className="review-spinner-ring" />
                  <p className="review-spinner-text">Loading reviews…</p>
                </div>
              )}

              {!reviewLoading && openReviews !== null && openReviews.length === 0 && (
                <div className="review-empty">
                  <span className="review-empty-icon">🛵</span>
                  <p>No reviews yet. Be the first!</p>
                </div>
              )}

              {!reviewLoading &&
                openReviews?.map((r, idx) => (
                  <div key={r._id} className="review-card" style={{ animationDelay: `${idx * 60}ms` }}>
                    <div className="review-card-header">
                      <div className="review-avatar">
                        {(r.userId?.name?.[0] ?? "?").toUpperCase()}
                      </div>
                      <div className="review-meta">
                        <span className="review-name">{r.userId?.name ?? "Anonymous"}</span>
                        <StarRating rating={r.rating} size={13} />
                      </div>
                      <span className="review-rating-pill">{r.rating}.0</span>
                    </div>
                    {r.comment && (
                      <p className="review-comment">{r.comment}</p>
                    )}
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- INLINE STYLES ---------- */
const styles = {
  rating: {
    marginTop: "6px",
    display: "flex",
    alignItems: "center",
  },
};

export default AvailableBikes;
